import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import { fetchVirksomhedsoplysninger, FALLBACK_VIRKSOMHED } from '../lib/cms';

export default function Footer() {
  const [virksomhed, setVirksomhed] = useState(FALLBACK_VIRKSOMHED);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    async function loadInfo() {
      const data = await fetchVirksomhedsoplysninger();
      if (data) setVirksomhed(data);
    }
    loadInfo();
  }, []);

  return (
    <footer className="bg-[#000300] text-slate-300 border-t border-neutral-900 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 pb-12 border-b border-slate-800/80">
          {/* Col 1: Brand & Logo */}
          <div className="space-y-4">
            <Link to="/" className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 rounded-md">
              <img
                src={imageUrl(virksomhed.logo?.[0] || '/_assets/duus-logo-transparent.png', 'width=380,height=120,quality=90,format=auto')}
                alt={`${virksomhed.firmanavn} Logo`}
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              {virksomhed.footer_tekst || 'Enkeltmandsvirksomhed med passion for solidt håndværk. Vi tilbyder alt fra små reparationer til skræddersyede specialløsninger i rustfrit stål, sort stål og aluminium.'}
            </p>
            {virksomhed.facebook_url && (
              <div className="pt-2">
                <a
                  href={virksomhed.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-sky-400 hover:text-sky-300 transition"
                  aria-label={`Besøg ${virksomhed.firmanavn} på Facebook`}
                >
                  <i className="fa-brands fa-facebook text-lg" aria-hidden="true" />
                  <span>Følg os på Facebook</span>
                </a>
              </div>
            )}
          </div>

          {/* Col 2: Navigation Links */}
          <div>
            <h3 className="text-white text-base font-semibold tracking-wide uppercase text-xs mb-4">
              Navigation
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="text-slate-400 hover:text-white transition">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/ydelser" className="text-slate-400 hover:text-white transition">
                  Ydelser & Produkter
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="text-slate-400 hover:text-white transition">
                  Referencer & Projekter
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="text-slate-400 hover:text-white transition">
                  Om Duus Rustfri
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-slate-400 hover:text-white transition">
                  Kontakt & Tilbud
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Relevante Ydelser */}
          <div>
            <h3 className="text-white text-base font-semibold tracking-wide uppercase text-xs mb-4">
              Ydelser
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/ydelser/specialfremstilling" className="text-slate-400 hover:text-white transition">
                  Specialfremstilling i stål
                </Link>
              </li>
              <li>
                <Link to="/ydelser/trug-og-foderkrybber" className="text-slate-400 hover:text-white transition">
                  Trug & foderkrybber
                </Link>
              </li>
              <li>
                <Link to="/ydelser/svejsning-og-roerarbejde" className="text-slate-400 hover:text-white transition">
                  Svejsning & rørarbejde
                </Link>
              </li>
              <li>
                <Link to="/ydelser/reparation-og-smedeopgaver" className="text-slate-400 hover:text-white transition">
                  Reparation & smedeopgaver
                </Link>
              </li>
              <li>
                <Link to="/ydelser/have-og-uderum" className="text-slate-400 hover:text-white transition">
                  Have- & uderumsløsninger
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Direkte Kontakt & Virksomhedsoplysninger */}
          <div className="space-y-4">
            <h3 className="text-white text-base font-semibold tracking-wide uppercase text-xs mb-4">
              Kontakt
            </h3>
            <div className="space-y-3 text-sm">
              <div>
                <p className="font-semibold text-white">{virksomhed.firmanavn}</p>
                <p className="text-slate-400">{virksomhed.adresse}</p>
                <p className="text-slate-400">{virksomhed.postnr_by}</p>
              </div>

              <div className="pt-1">
                <span className="block text-xs uppercase text-slate-500 font-medium">Telefon</span>
                <a
                  href={`tel:${virksomhed.telefon.replace(/\s+/g, '')}`}
                  className="font-semibold text-sky-400 hover:text-sky-300 transition whitespace-nowrap block"
                >
                  {virksomhed.telefon}
                </a>
              </div>

              <div>
                <span className="block text-xs uppercase text-slate-500 font-medium">Email</span>
                <a
                  href={`mailto:${virksomhed.email}`}
                  className="font-semibold text-sky-400 hover:text-sky-300 transition whitespace-nowrap block"
                >
                  {virksomhed.email}
                </a>
              </div>

              <div>
                <span className="block text-xs uppercase text-slate-500 font-medium">CVR</span>
                <span className="font-mono text-slate-400 whitespace-nowrap">{virksomhed.cvr}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>© {currentYear} {virksomhed.firmanavn}. Alle rettigheder forbeholdes.</p>
          <p className="flex items-center gap-1.5">
            <i className="fa-solid fa-shield-halved text-slate-600" aria-hidden="true" />
            <span>Kvalitetshåndværk i rustfrit stål, sort stål & aluminium</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
